import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store/store';
import { styles } from './ProducerNotesWidget.styles';
import {
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
  LayoutAnimation,
  TextInput,
} from 'react-native';
import Typography from 'src/components/Text/Text';
import { useUpdateCompanyNotesMutation, useGetCompanyNotesQuery } from '../../api/companyApi';

const ProducerNotesWidget: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [notes, setNotes] = useState('');

  const currentCompanyId = useSelector((state: RootState) => state.company.company?.id);
  const companyName = useSelector((state: RootState) => state.company.company?.companyName);
  const producerFormId = useSelector((state: RootState) => state.users.producerFormId);

  const [updateCompanyNotes, { isLoading }] = useUpdateCompanyNotesMutation();
  const { data: savedNotes, isLoading: isFetchingNotes } = useGetCompanyNotesQuery(
    currentCompanyId!.toString(),
  );

  const toggleExpand = () => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setIsExpanded((prev) => !prev);
    if (!isExpanded) {
      // When opening, load the saved notes
      setNotes(savedNotes || '');
    }
  };

  const handleClose = () => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setIsExpanded(false);
    // Reset to saved notes on cancel
    setNotes(savedNotes || '');
  };

  const handleSave = async () => {
    if (!currentCompanyId) {
      console.error('No company ID available');
      return;
    }

    try {
      await updateCompanyNotes({
        companyId: currentCompanyId.toString(),
        note: notes,
        producerFormId: producerFormId || '',
      }).unwrap();

      console.log('Notes saved successfully for company:', currentCompanyId);
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      setIsExpanded(false);
    } catch (error) {
      console.error('Failed to save notes:', error);
      // You can add error toast here if needed
    }
  };

  const handleNotesChange = (text: string) => {
    setNotes(text);
  };

  return (
    <>
      {!isExpanded ? (
        // Floating Button
        <TouchableOpacity
          style={styles.floatingButton}
          onPress={toggleExpand}
          data-testid="producer-notes-button">
          <Typography variant="h4" style={styles.buttonText}>
            Notes
          </Typography>
        </TouchableOpacity>
      ) : (
        // Expanded Panel
        <TouchableWithoutFeedback onPress={handleClose} data-testid="producer-notes-overlay">
          <View style={styles.overlay}>
            <TouchableWithoutFeedback>
              <View
                style={styles.expandedContainer}
                data-testid="producer-notes-expanded-container">
                <Typography variant="h4" style={styles.title} data-testid="producer-notes-title">
                  Producer Notes {companyName ? `- ${companyName}` : ''}
                </Typography>

                {isFetchingNotes ? (
                  <View style={styles.loadingContainer} data-testid="producer-notes-loading">
                    <Typography style={styles.loadingText}>Loading notes...</Typography>
                  </View>
                ) : (
                  <TextInput
                    style={styles.textInput}
                    placeholder="Add notes about this company..."
                    placeholderTextColor="#999"
                    multiline
                    value={notes}
                    onChangeText={handleNotesChange}
                    data-testid="producer-notes-input"
                  />
                )}

                <View style={styles.buttonContainer}>
                  <TouchableOpacity
                    style={styles.cancelButton}
                    onPress={handleClose}
                    disabled={isLoading}
                    data-testid="producer-notes-cancel-button">
                    <Typography style={styles.cancelButtonText}>Cancel</Typography>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.saveButton}
                    onPress={handleSave}
                    disabled={isLoading}
                    data-testid="producer-notes-save-button">
                    <Typography style={styles.saveButtonText}>Save</Typography>
                  </TouchableOpacity>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      )}
    </>
  );
};

export default ProducerNotesWidget;
